'use client'

import * as React from 'react'
import { useTranslations } from 'next-intl'
import * as Popover from 'primitives/popover'
import * as Dialog from 'primitives/dialog'
import { IconButton } from 'primitives/buttons/icon-button'
import { motion } from 'framer-motion'
import { Button } from '@repo/design-system-cf/components/button'
import { useRouter } from 'next/navigation'
import { AppRoutes } from '@repo/web-utils/app-routes'
import { useFeatureIsOn } from 'growthbook'

import {
  ElipsisMenuIcon,
  CloseIcon,
  LeaveClubhouseIcon,
  NotificationSettingsIcon,
} from '@web/app/[locale]/clubhouse/assets/icons'
import { useClubhousesGetDetailsQuery } from '@web/gen/clubhouses'
import { FEATURE_CLUBHOUSES_NOTIFICATIONS_SETTINGS } from '@web/app/[locale]/clubhouse/_utils/features'

const transitionAnim = {
  initial: { opacity: 0, scale: 0.95 },
  animate: { opacity: 1, scale: 1 },
  exit: { opacity: 0, scale: 0.95 },
  transition: { duration: 0.2 },
}

export const ClubhouseSettingsPopover = ({
  clubhouseSlug,
  handleLeave,
}: {
  clubhouseSlug: string
  handleLeave: () => void
}) => {
  const router = useRouter()
  const t = useTranslations('pages.clubhouse.topbanner')
  const isNotificationSettingsOn = useFeatureIsOn(FEATURE_CLUBHOUSES_NOTIFICATIONS_SETTINGS)

  const { data: clubhouseName } = useClubhousesGetDetailsQuery(
    {
      input: {
        clubhouseSlug,
      },
    },
    {
      select: data => data?.clubhousesGetDetails?.clubhouse?.name,
    },
  )

  const handleOpenSettings = () => {
    router.push(`${AppRoutes.CLUBHOUSE}/${clubhouseSlug}/notifications`)
  }

  return (
    <motion.div key="popover" {...transitionAnim}>
      <Popover.Root>
        <Popover.Trigger asChild>
          <IconButton
            data-testid="clubhouse-settings-popover-trigger"
            className="bg-cf-white active:text-legacy-cf-purple-100 border-legacy-cf-purple-100 text-legacy-cf-purple-100 rounded-2 h-9 border-2 hover:bg-[#F3EEFE] active:bg-[#F3EEFE]"
          >
            <ElipsisMenuIcon className="size-6" />
          </IconButton>
        </Popover.Trigger>
        <Popover.Content
          side="bottom"
          align="end"
          className="bg-cf-white rounded-2 w-[240px] shadow-[0px_2px_6px_2px_rgba(0,0,0,0.125)]"
        >
          <div className="p-1">
            <Dialog.Root>
              <Dialog.Trigger asChild>
                <button
                  data-testid="clubhouse-settings-dialog-leave-trigger"
                  className="text-cf-sf-gray-1100 hover:bg-cf-sf-gray-75 text-14 active:bg-cf-sf-gray-150 flex w-full flex-row items-center gap-2 rounded-[4px] px-2 py-2.5 text-left font-medium leading-4"
                >
                  <LeaveClubhouseIcon />
                  {t('leave_clubhouse')}
                </button>
              </Dialog.Trigger>
              <React.Suspense fallback={null}>
                <Dialog.Portal forceMount>
                  <Dialog.Body className="font-poppins xl:overflow-y-auto xl:py-10" zIndex={2000}>
                    <div className="relative w-full max-w-[656px]">
                      <div className="bg-cf-white rounded-2 relative flex flex-col overflow-y-auto">
                        <div className="border-cf-sf-gray-350 flex items-center justify-between border-b px-6 py-4">
                          <p className="text-cf-sf-gray-1050 text-[18px] font-bold leading-[26px]">
                            {t('leave_clubhouse')}?
                          </p>
                          <Dialog.Close asChild>
                            <IconButton className="size-8 rounded-[50%] bg-transparent text-[#4B5563] transition-transform hover:scale-110 focus:bg-transparent focus:ring-0 focus-visible:outline-none active:scale-100 active:bg-transparent active:text-[#4B5563]">
                              <CloseIcon className="size-6" />
                            </IconButton>
                          </Dialog.Close>
                        </div>
                        <div className="p-6">
                          <p className="text-16 text-cf-sf-gray-1050 pb-6 font-medium sm:font-semibold">
                            {t('leave_dialog_text', { clubhouse: clubhouseName ?? '' })}
                          </p>
                          <div className="flex items-center justify-end gap-3">
                            <Dialog.Close asChild>
                              <button className="text-legacy-cf-purple-100 font-medium hover:text-[#2A1F3F]">
                                Cancel
                              </button>
                            </Dialog.Close>
                            <Button
                              onClick={handleLeave}
                              className="rounded-2 text-14 p-2 font-semibold"
                              data-testid="clubhouse-setting-dialog-leave-button"
                            >
                              {t('leave_clubhouse')}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Dialog.Body>
                </Dialog.Portal>
              </React.Suspense>
            </Dialog.Root>
            {isNotificationSettingsOn ? (
              <button
                onClick={handleOpenSettings}
                className="text-cf-sf-gray-1100 hover:bg-cf-sf-gray-75 text-14 active:bg-cf-sf-gray-150 flex w-full flex-row items-center gap-2 rounded-[4px] p-2 px-2 py-2.5 text-left font-medium leading-4"
              >
                <NotificationSettingsIcon />
                {t('notifications_settings')}
              </button>
            ) : null}
          </div>
        </Popover.Content>
      </Popover.Root>
    </motion.div>
  )
}
